import React, { useState } from 'react'

import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'

import { apiGetEventorPersons, apiSetEventorPerson, ClubPerson, EventorPersonUser } from './Api'
import { useApiData, Loading } from './useApiData'
import { addToCallChain } from './CallCenter'
import ConfirmDialog from './ConfirmDialog'
import SelectInput from './SelectInput'

/**
 * Which person in Eventor each Eko user is.
 *
 * The connection lets Eko open Eventor already signed in as the person doing the work — so nobody
 * types an Eventor password, and what Eventor records as having been done by a person really was
 * done by them. Before this, Eko wrote into Eventor as whoever the browser tab happened to be
 * signed in as, which on a shared computer is not necessarily the person in Eko.
 *
 * An administrator makes the connection and only an administrator can: it says whose Eventor
 * session Eko may hand out, and a picker each user could set for themselves would be a picker for
 * whose identity to borrow. That is also why changing one is a dialog rather than a select that
 * saves as it slides past.
 */
export default function EventorPersons() {
    const { data, loading, reload } = useApiData(apiGetEventorPersons, [])
    // The change awaiting confirmation: which user, and which person they would become.
    const [pending, setPending] = useState<{ user: EventorPersonUser, person: ClubPerson | null } | null>(null)

    if (data === null) return <Loading/>

    const label = (person: ClubPerson) =>
        person.year_of_birth === null ? person.name : `${person.name} (${person.year_of_birth})`
    const byId = new Map(data.persons.map(person => [person.person_id, person]))

    const apply = () => {
        if (pending === null) return
        const { user, person } = pending
        setPending(null)
        // Through the call chain, like every other mutation: it locks the interface while the
        // write is in flight and routes a failure to the error bus.
        addToCallChain(
            () => apiSetEventorPerson(user.id, person?.person_id ?? null).then(reload), true)
    }

    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', gap: '0.75rem',
                      width: '100%', maxWidth: '60rem' }}>
            <Alert severity="info">
                Kopplingen gör att Eko kan öppna Eventor-fliken färdiginloggad som den som arbetar,
                och att det som görs i Eventor står på rätt person. Den som inte är kopplad loggar
                in i Eventor själv, precis som förut.
            </Alert>

            <Table size="small">
                <TableHead><TableRow>
                    <TableCell>Användare i Eko</TableCell>
                    <TableCell>Person i Eventor</TableCell>
                    <TableCell>Kopplad av</TableCell>
                </TableRow></TableHead>
                <TableBody>
                    {data.users.map(user =>
                        <TableRow key={user.id}>
                            <TableCell>
                                {user.name}
                                <div style={{ color: '#777', fontSize: '0.85em' }}>{user.email}</div>
                            </TableCell>
                            <TableCell>
                                {/* Namesakes are why the year of birth is shown: picking the wrong
                                    one here hands somebody a session as a person they are not. */}
                                <SelectInput<string, ClubPerson, never>
                                    label="Ingen" required={false} sx={{ minWidth: 300 }}
                                    disabled={loading}
                                    options={data.persons}
                                    valueGetter={person => person.person_id}
                                    nameGetter={label}
                                    value={user.person_id}
                                    onChange={personId => {
                                        if (personId === user.person_id) return
                                        setPending({ user, person: personId === null ? null
                                                                  : byId.get(personId) ?? null })
                                    }}/>
                            </TableCell>
                            <TableCell style={{ color: '#777', fontSize: '0.85em' }}>
                                {user.assigned_by === null ? '—'
                                 : `${user.assigned_by}, ${user.assigned_at ?? ''}`}
                            </TableCell>
                        </TableRow>)}
                </TableBody>
            </Table>

            <ConfirmDialog
                open={pending !== null}
                title={pending?.person == null ? 'Ta bort kopplingen?' : 'Koppla till Eventor?'}
                confirmLabel={pending?.person == null ? 'Ta bort' : 'Koppla'}
                message={pending === null ? null : pending.person === null ? <>
                    <p>
                        <strong>{pending.user.name}</strong> kopplas inte längre till någon person
                        i Eventor, och får logga in i Eventor själv.
                    </p>
                </> : <>
                    <p>
                        Eko kommer att kunna öppna Eventor som <strong>{label(pending.person)}</strong>
                        {' '}när <strong>{pending.user.name}</strong> hämtar något — med den
                        personens rättigheter, och i den personens namn.
                    </p>
                    <p>
                        Rättigheterna i Eventor följer inte med kopplingen. Personen behöver vara
                        administratör för arrangerande förening i Eventor för att kunna hämta
                        bokföringsunderlag.
                    </p>
                </>}
                onCancel={() => setPending(null)}
                onConfirm={apply}/>
        </div>
    )
}
